import SharedWorkspaceHeader from './components/SharedWorkspaceHeader';
import AccountProfile from './components/AccountProfile';
import {accountLabel,loadOrdinarySharedProfile,type OrdinarySharedProfile} from './lib/ordinarySharedProfile';
import OrdinaryWorkforceHome from './components/OrdinaryWorkforceHome';
import WidgetHome from './components/WidgetHome';
import WorkspaceNotes from './components/WorkspaceNotes';
import MoreHub,{type MoreSection} from './components/MoreHub';
import SkillsWorkspace from './components/SkillsWorkspace';
import AgentChatsHub from './components/AgentChatsHub';
import PublicDesignPreview from './components/PublicDesignPreview';
import AgentBriefingCard from './components/AgentBriefingCard';
import {pauseEntireWorkforce,resumeEntireWorkforce,pauseOneEmployee,updateEmployeeNickname,runEmployeeNow} from './lib/ownerControls';
import {
  ArrowLeft,
  CircleAlert,
  Link2,
  LogOut,
  RefreshCw,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import Brand from './components/Brand';
import ThemePicker, { initialTheme, syncBrowserTheme, type Theme } from './components/ThemePicker';
import ScheduleCalendar from './components/ScheduleCalendar';
import CharacterStudio from './components/CharacterStudio';
import { Connections, Guardrail, LiveWork } from './components/Operations';
import Workforce from './components/Workforce';
// Read-only workspace display data is separate from canonical SALRI runtime mutations.
import {
  getPrimaryMembership,
  getSafetyState,
  listAgents,
  listCharacterProfiles,
} from './lib/agentordinaryWorkspace';
import { supabase } from './lib/supabase';
import type { Agent, CharacterProfile, SafetyState } from './types';

type Page =
  | 'Agents'
  | 'Schedule'
  | 'Settings'
  | 'Profile'
  | 'More'
  | 'Toolbox'
  | 'Agent Chats'
  | 'Skills'
  | 'Home'
  | 'Workforce'
  | 'Live Work'
  | 'Connections'
  | 'Guardrail'
  | 'Notes'
  | 'Character Studio';

// The same production hostname serves a public showcase when signed out and the owner's
// authenticated workspace after sign-in. Explicit ?owner=1 requests the sign-in form.
const OWNER_HOST = 'agentordinary-review.pages.dev';
const ownerUrl = (path = '/?owner=1') => new URL(path, 'https://' + OWNER_HOST).toString();
// Auth is stored per browser origin. Never send owner sign-in or OAuth to a deployment-alias subdomain.
if (typeof window !== 'undefined' && window.location.hostname.endsWith('.agentordinary-review.pages.dev')
  && new URLSearchParams(window.location.search).get('owner') === '1') {
  window.location.replace(ownerUrl(window.location.pathname + window.location.search + window.location.hash));
}
// Query flags are a navigation request, never proof of authentication.
const isLoginRequested = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('owner') === '1';

const recoveryRedirect = () => ownerUrl('/?owner=1&recovery=1');

function App() {
  const [page, setPage] = useState<Page>('Home');
  const [pageTrail, setPageTrail] = useState<Page[]>([]);
  const [briefingAgentId, setBriefingAgentId] = useState<string | null>(null);
  const [returnAgentId,setReturnAgentId]=useState<string|null>(null);
  const [scheduleFocusId,setScheduleFocusId]=useState<string | undefined>();
  function navigateTo(destination:Page){
    if(page!==destination)setPageTrail(trail=>[...trail.slice(-24),page]);
    setBriefingAgentId(null);
    setReturnAgentId(null);
    setPage(destination);
  }
  function navigateFromAgent(destination:Page,agentId:string){
    if(page!==destination)setPageTrail(trail=>[...trail.slice(-24),page]);
    setBriefingAgentId(null);
    setReturnAgentId(agentId);
    setPage(destination);
  }
  function goBack(){
    if(briefingAgentId){setBriefingAgentId(null);return;}
    const previous=pageTrail.at(-1);
    if(previous){
      setPageTrail(trail=>trail.slice(0,-1));setPage(previous);
      if(returnAgentId){setBriefingAgentId(returnAgentId);setReturnAgentId(null)}
      return;
    }
    if(page!=='Home')setPage('Home');
  }
  const [theme, setTheme] = useState<Theme>(initialTheme);
  const dark = theme.endsWith('dark');
  useEffect(() => { syncBrowserTheme(theme); }, [theme]);
  function changeTheme(value: Theme) {
    setTheme(value);
    try { localStorage.setItem('agentordinary-theme', value); } catch {}
  }
  const [sessionReady, setSessionReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [accountProfile,setAccountProfile]=useState<OrdinarySharedProfile|null>(null);
  const [accountProfileLoading,setAccountProfileLoading]=useState(false);
  const [accountProfileError,setAccountProfileError]=useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [recoverySession, setRecoverySession] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [profiles, setProfiles] = useState<CharacterProfile[]>([]);
  const [safetyState, setSafetyState] = useState<SafetyState | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState('');

  // The owner roster includes all canonical employees, even those paused or disabled.
  const workforce = useMemo(() => agents, [agents]);
  const selectedAgent = useMemo(
    () => workforce.find(agent => agent.id === selectedAgentId),
    [workforce, selectedAgentId],
  );
  const briefingAgent=workforce.find(agent=>agent.id===briefingAgentId);
  async function ownerPauseWorkforce(){
   if(!workspaceId)throw new Error('Workspace not loaded');
   await pauseEntireWorkforce(workspaceId);
   await refresh();
  }
  async function ownerResumeWorkforce(){
   if(!workspaceId)throw new Error('Workspace not loaded');
   await resumeEntireWorkforce(workspaceId);
   await refresh();
  }
  async function ownerPauseAgent(agentId:string){
   if(!workspaceId)throw new Error('Workspace not loaded');
   await pauseOneEmployee(workspaceId,agentId);
   await refresh();
  }
  async function ownerChangeNickname(agentId:string,nickname:string){
   if(!workspaceId)throw new Error('Workspace not loaded');
   const current=agents.find(agent=>agent.id===agentId);
   if(!current)throw new Error('Employee not found');
   const result=await updateEmployeeNickname(workspaceId,current,nickname);
   setAgents(prev=>prev.map(agent=>agent.id===agentId?{...agent,nickname:result||null,revision:agent.revision+1}:agent));
  }
  const linkedCount = profiles.filter(profile =>
    workforce.some(agent => agent.id === profile.agent_id),
  ).length;

  useEffect(() => {
    let active = true;
    const url = new URL(window.location.href);
    const directRecoveryToken =
      url.searchParams.get('token_hash') || url.searchParams.get('token');
    const urlLooksLikeRecovery =
      url.searchParams.get('recovery') === '1' ||
      url.searchParams.get('type') === 'recovery' ||
      url.hash.includes('type=recovery') ||
      Boolean(directRecoveryToken);

    if (urlLooksLikeRecovery) setRecovering(true);

    async function bootstrapAuth() {
      if (directRecoveryToken) {
        const { data, error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: directRecoveryToken,
          type: 'recovery',
        });
        if (!active) return;
        if (verifyError || !data.session) {
          setError(verifyError?.message || 'Recovery link could not be verified.');
          setSessionReady(true);
          return;
        }
        setSignedIn(true);
        setRecovering(true);
        setRecoverySession(true);
        setAuthNotice('Recovery verified. Set your new AgentOrdinary password below.');
        window.history.replaceState({}, '', '/?owner=1&recovery=1');
        setSessionReady(true);
        return;
      }

      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setSignedIn(Boolean(data.session));
      if (urlLooksLikeRecovery && data.session) setRecoverySession(true);
      setSessionReady(true);
    }

    void bootstrapAuth();

    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      setSignedIn(Boolean(session));
      if (event === 'PASSWORD_RECOVERY') {
        setRecovering(true);
        setRecoverySession(true);
        setAuthNotice('Recovery verified. Set your new AgentOrdinary password below.');
      }
      setSessionReady(true);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!signedIn || recoverySession) {
      if (!signedIn) {
        setWorkspaceId('');
        setAgents([]);
        setProfiles([]);
        setSafetyState(null);
        setSelectedAgentId('');
      }
      return;
    }
    void refresh();
  }, [signedIn, recoverySession]);

  // Load the signed-in user's own shared profile, never the public preview.
  useEffect(()=>{
   if(!signedIn||recoverySession){
    setAccountProfile(null);setAccountProfileError('');setAccountProfileLoading(false);return;
   }
   let active=true;setAccountProfileLoading(true);
   void loadOrdinarySharedProfile().then(profile=>{
    if(active){setAccountProfile(profile);setAccountProfileError('');}
   }).catch(error=>{
    if(active)setAccountProfileError(error instanceof Error?error.message:'Unable to load account profile.');
   }).finally(()=>{if(active)setAccountProfileLoading(false)});
   return ()=>{active=false};
  },[signedIn,recoverySession]);

  async function refresh() {
    setLoadingData(true);
    setError('');
    try {
      const membership = await getPrimaryMembership();
      const [nextAgents, nextProfiles, nextSafety] = await Promise.all([
        listAgents(membership.workspace_id),
        listCharacterProfiles(membership.workspace_id),
        getSafetyState(membership.workspace_id),
      ]);
      const nextWorkforce = nextAgents;
      setWorkspaceId(membership.workspace_id);
      setAgents(nextAgents);
      setProfiles(nextProfiles);
      setSafetyState(nextSafety);
      setSelectedAgentId(current =>
        nextWorkforce.some(agent => agent.id === current)
          ? current
          : nextWorkforce[0]?.id ?? '',
      );
    } catch (problem) {
      setError(
        problem instanceof Error
          ? 'AgentOrdinary workspace could not be loaded: ' + problem.message
          : 'AgentOrdinary workspace could not be loaded. Use Refresh to retry.',
      );
    } finally {
      setLoadingData(false);
    }
  }

  async function signInWithGoogle() {
    setError('');
    setAuthNotice('');
    const { error: authError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: ownerUrl() },
    });
    if (authError) setError(authError.message);
  }

  async function signInWithPassword() {
    setError('');
    setAuthNotice('');
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError('Enter your AgentOrdinary account email.');
      return;
    }
    if (!password) {
      setError('Enter your AgentOrdinary password.');
      return;
    }

    const { error: authError } = await supabase.auth.signInWithPassword({
      email: normalized,
      password,
    });

    if (authError) {
      setError(authError.message);
      return;
    }

    setPassword('');
    window.history.replaceState({}, '', '/');
    setAuthNotice('Signed in. AgentOrdinary will keep this session on this device.');
  }

  async function sendRecoveryEmail() {
    setError('');
    setAuthNotice('');
    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      setError('Enter your AgentOrdinary account email first.');
      return;
    }

    const { error: recoveryError } = await supabase.auth.resetPasswordForEmail(
      normalized,
      { redirectTo: recoveryRedirect() },
    );

    if (recoveryError) {
      setError(recoveryError.message);
      return;
    }

    setRecovering(true);
    setRecoverySession(false);
    setAuthNotice(
      'Password reset email sent. Tap the newest reset link; AgentOrdinary will open the new-password screen automatically.',
    );
  }

  async function finishRecovery() {
    setError('');
    setAuthNotice('');

    if (!recoverySession) {
      setError('Open the newest AgentOrdinary password-reset email first.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Use a password with at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      setError(updateError.message);
      return;
    }

    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setRecovering(false);
    setRecoverySession(false);
    setAuthNotice('Password changed. You are signed in.');
    window.history.replaceState({}, '', '/?owner=1');
    await refresh();
  }

  async function signOut() {
    const {error: signOutError} = await supabase.auth.signOut();
    if(signOutError){setError(signOutError.message);return;}
    // Return to the exact same website's public experience, not a second deployment.
    window.location.replace('/');
  }

  function updateCharacterProfile(profile: CharacterProfile) {
    setProfiles(previous => [
      profile,
      ...previous.filter(item => item.agent_id !== profile.agent_id),
    ]);
  }

  function updateAgent(saved: Agent) {
    setAgents(previous =>
      previous.map(agent => (agent.id === saved.id ? saved : agent)),
    );
  }

  if (!sessionReady) {
    return <div className="boot">Loading AgentOrdinary...</div>;
  }

  if (recoverySession) {
    return (
      <div className={'app ordinary ' + (dark ? 'dark' : 'light')} data-theme={theme}>
        <div className="shell">
          <header className="topbar">
            <Brand />
            <ThemePicker value={theme} onChange={changeTheme} />
          </header>
          <main className="signin-wrap">
            <section className="signin-card">
              <div className="eyebrow">PASSWORD RECOVERY</div>
              <h1>Set your new AgentOrdinary password.</h1>
              <p>The reset link is verified. No second email link is required.</p>
              {error && <div className="message error"><CircleAlert size={17} />{error}</div>}
              {authNotice && <div className="message success">{authNotice}</div>}
              <label>
                New password
                <input
                  type="password"
                  value={newPassword}
                  onChange={event => setNewPassword(event.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
              </label>
              <label>
                Confirm new password
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={event => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter new password"
                  autoComplete="new-password"
                  onKeyDown={event => {
                    if (event.key === 'Enter') void finishRecovery();
                  }}
                />
              </label>
              <button className="primary-action" onClick={() => void finishRecovery()}>
                Save new password
              </button>
            </section>
          </main>
        </div>
      </div>
    );
  }

  // The live auth session determines which experience is available.
  // Signed-out visitors see the approved public five-agent view unless opening Sign in.
  if (!signedIn && !isLoginRequested && !recovering) return <PublicDesignPreview />;

  if (!signedIn) {
    return (
      <div className={'app ordinary ' + (dark ? 'dark' : 'light')} data-theme={theme}>
        <div className="shell">
          <header className="topbar">
            <Brand />
            <ThemePicker value={theme} onChange={changeTheme} />
          </header>
          <main className="signin-wrap">
            <section className="signin-card">
              <div className="eyebrow">AGENTORDINARY</div>
              <h1>Sign in to Agentordinary</h1>
              <p>
                Access your agents, skills, and workspace.
              </p>
              {error && <div className="message error"><CircleAlert size={17} />{error}</div>}
              {authNotice && <div className="message success">{authNotice}</div>}
              <a className="outline-action" style={{display:'flex',justifyContent:'center',textDecoration:'none'}} href="/?preview=more">Back to site</a>
              <button className="primary-action" type="button" onClick={() => void signInWithGoogle()}>
                Continue with Google
              </button>
              <p className="muted">Or sign in with an email and password you have set for this account.</p>
              <label>
                Email
                <input
                  type="email"
                  value={email}
                  onChange={event => setEmail(event.target.value)}
                  placeholder="Email address"
                  autoComplete="username"
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  value={password}
                  onChange={event => setPassword(event.target.value)}
                  placeholder="Password"
                  autoComplete="current-password"
                  onKeyDown={event => {
                    if (event.key === 'Enter') void signInWithPassword();
                  }}
                />
              </label>
              <button className="primary-action" onClick={() => void signInWithPassword()}>
                Sign in
              </button>
              <button
                className="outline-action"
                onClick={() => void sendRecoveryEmail()}
              >
                Forgot password / reset access
              </button>
              {recovering && !recoverySession && (
                <p className="muted">
                  Use the newest reset email. Older reset links may be expired.
                </p>
              )}
            </section>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={'app ordinary preview-design ' + (dark ? 'dark' : 'light') + (page === 'Home' ? ' home-route' : '')} data-theme={theme}>
      <div className="shell">
        <SharedWorkspaceHeader current={page === 'Schedule' ? 'Schedule' : page === 'Home' ? 'Home' : ['Agents','Workforce','Character Studio'].includes(page) ? 'Agents' : 'More'} loading={loadingData} theme={theme} onThemeChange={changeTheme} onNavigate={destination => navigateTo(destination)}>
          <button type="button" className="outline-action" title="Open your profile" onClick={()=>navigateTo('Profile')} style={{maxWidth:175,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'inline-flex',gap:6,alignItems:'center'}}><UserRound size={16}/><span style={{overflow:'hidden',textOverflow:'ellipsis'}}>{accountLabel(accountProfile)}</span></button>
          <button className="icon-button" onClick={() => void refresh()} disabled={loadingData}>
            <RefreshCw size={18} className={loadingData ? 'spin' : ''} />
          </button>
          <button className="outline-action" onClick={signOut}><LogOut size={16}/>Sign out</button>
        </SharedWorkspaceHeader>
        {safetyState&&<div className="ao-workforce-status-row"><span className={safetyState.global_paused?'ao-status-paused':'ao-status-armed'}>{safetyState.global_paused?'WORKFORCE PAUSED':'WORKFORCE ACTIVE'}</span></div>}



        {error && signedIn && (
          <div className="message error"><CircleAlert size={17} />{error}</div>
        )}

        <main className="preview-content">
          <div className="pdp-universal-back"><button type="button" onClick={goBack} aria-label="Back to previous screen"><ArrowLeft size={17}/>Back</button><small>{page==='Workforce'?'Employee settings':page}</small></div>
          {page === 'Schedule' && <ScheduleCalendar employees={workforce.map(agent=>({id:agent.id,name:agent.official_name,nickname:agent.nickname||undefined,role:agent.title,roleKey:agent.role_key,profile:profiles.find(p=>p.agent_id===agent.id)}))} workspaceId={workspaceId||undefined} initialEmployeeId={scheduleFocusId} />}
          {page === 'More' && <MoreHub onSelect={(section:MoreSection)=>navigateTo(section==='Activity Log'?'Live Work':section==='Guardrails'?'Guardrail':section)} />}
          {page === 'Agent Chats' && workspaceId && <AgentChatsHub workspaceId={workspaceId} previewOnly={false} onBack={()=>navigateTo('More')} onOpenConnections={()=>navigateTo('Connections')}
           roster={workforce.map(a=>({id:a.id,name:a.official_name,role:a.title,roleKey:a.role_key,nickname:a.nickname,
            status:a.status,department:a.department||undefined,profile:profiles.find(p=>p.agent_id===a.id)}))} />}
          {page === 'Skills' && <SkillsWorkspace employees={agents.map(a=>({id:a.id,name:a.official_name,nickname:a.nickname||undefined,role:a.title,roleKey:a.role_key,status:a.status,enabled:a.enabled}))} existingProfiles={agents.map(a=>({roleKey:a.role_key,skills:Array.isArray(a.profile?.skills)?a.profile.skills:[]}))} onBack={()=>navigateTo('More')} previewOnly={false}/>}
          {page === 'Toolbox' && <section className="panel"><h1>Toolbox</h1><div className="more-sections"><button type="button" className="outline-action" onClick={()=>navigateTo('Live Work')}>Activity Logs</button><button type="button" className="outline-action" onClick={()=>navigateTo('Guardrail')}>Guardrails</button><button type="button" className="outline-action" onClick={()=>navigateTo('Connections')}>Connections</button></div></section>}
          {page === 'Settings' && <section className="panel"><h1>Settings</h1><p className="muted">Choose your workspace appearance.</p><ThemePicker value={theme} onChange={changeTheme} /></section>}
          {page === 'Profile' && <AccountProfile profile={accountProfile} loading={accountProfileLoading} loadError={accountProfileError} onSaved={setAccountProfile}/>}

          {page === 'Home' && workspaceId && <WidgetHome workspaceId={workspaceId} agents={workforce} profiles={profiles} safetyState={safetyState} onAgents={()=>navigateTo('Agents')} onAgentProfile={id=>{setSelectedAgentId(id);navigateTo('Home');setBriefingAgentId(id)}} onSchedule={()=>navigateTo('Schedule')} onNotes={()=>navigateTo('Notes')} onActivity={()=>navigateTo('Live Work')} />}

          {page === 'Agents' && <OrdinaryWorkforceHome employees={workforce.map(agent=>({id:agent.id,name:agent.official_name,nickname:agent.nickname||undefined,role:agent.title,roleKey:agent.role_key,status:agent.status,profile:profiles.find(p=>p.agent_id===agent.id)}))} total={workforce.length} paused={safetyState?.global_paused} onGlobalPause={ownerPauseWorkforce} onGlobalResume={ownerResumeWorkforce} onSelect={id=>{setSelectedAgentId(id);setBriefingAgentId(id);}} onSchedule={()=>navigateTo('Schedule')} onActivity={()=>navigateTo('Live Work')} onStudio={()=>navigateTo('Character Studio')}/>}

          {page === 'Workforce' && workspaceId && (
            <Workforce
              workspaceId={workspaceId}
              agents={workforce}
              profiles={profiles}
              selectedAgentId={selectedAgentId}
              onSelectAgent={setSelectedAgentId}
              onAgentSaved={updateAgent}
              onOpenConnections={()=>navigateTo('Connections')}
            />
          )}

          {page === 'Live Work' && workspaceId && (
            <LiveWork workspaceId={workspaceId} agents={workforce} />
          )}

          {page === 'Notes' && workspaceId && <WorkspaceNotes workspaceId={workspaceId} />}

          {page === 'Connections' && workspaceId && (
            <Connections workspaceId={workspaceId} />
          )}

          {page === 'Guardrail' && workspaceId && (
            <Guardrail workspaceId={workspaceId} />
          )}

          {page === 'Character Studio' && workspaceId && (
            <CharacterStudio
              workspaceId={workspaceId}
              agents={workforce}
              profiles={profiles}
              selectedAgentId={selectedAgentId}
              onSelectAgent={setSelectedAgentId}
              onProfileSaved={updateCharacterProfile}
              onAgentSaved={updateAgent}
            />
          )}
        </main>

      </div>
      {briefingAgent&&workspaceId&&<div className="pdp-dialog-screen" onClick={()=>setBriefingAgentId(null)}>
        <div className="pdp-agent-card-frame" onClick={event=>event.stopPropagation()}>
          <AgentBriefingCard key={briefingAgent.id}
            employee={{id:briefingAgent.id,name:briefingAgent.official_name,nickname:briefingAgent.nickname,role:briefingAgent.title,roleKey:briefingAgent.role_key,status:briefingAgent.status,enabled:briefingAgent.enabled,department:briefingAgent.department||undefined,profile:profiles.find(profile=>profile.agent_id===briefingAgent.id),tools:briefingAgent.profile?.tools}}
            liveProfileSkills={briefingAgent.profile?.skills||[]}
            roster={workforce.map(agent=>({id:agent.id,name:agent.official_name,nickname:agent.nickname,role:agent.title,roleKey:agent.role_key,status:agent.status,department:agent.department||undefined,profile:profiles.find(profile=>profile.agent_id===agent.id),tools:agent.profile?.tools}))}
            workspaceId={workspaceId} previewOnly={false}
            onClose={()=>setBriefingAgentId(null)}
            onSchedule={()=>{setScheduleFocusId(briefingAgent.id);navigateTo('Schedule')}}
            onSaveNickname={nickname=>ownerChangeNickname(briefingAgent.id,nickname)}
            onPauseAgent={()=>ownerPauseAgent(briefingAgent.id)}
            onRunAgent={(instruction,dispatchId)=>runEmployeeNow(workspaceId,briefingAgent.id,instruction,dispatchId)}
            onStudio={()=>{setSelectedAgentId(briefingAgent.id);navigateFromAgent('Character Studio',briefingAgent.id)}}
            onOpenConnections={()=>navigateFromAgent('Connections',briefingAgent.id)}
            onEditProfile={()=>{setSelectedAgentId(briefingAgent.id);navigateFromAgent('Workforce',briefingAgent.id)}}
          />
        </div>
      </div>}
    </div>
  );
}

export default App;

